import { useState } from "react";
import clsx from "clsx";
import { SupplierLogo } from "@/components/GeschaeftspartnerLogo";
import { Button } from "@/components/ui/Button";
import type { OrderDraft } from "@/lib/orders";
import { formatAuftragsnummerDisplay } from "@/lib/orderDraftUtils";

function fmtDate(value: string | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function draftContact(d: OrderDraft): string {
  return (
    [d.supplier_contact_anrede, d.supplier_contact_first_name, d.supplier_contact_last_name]
      .filter(Boolean)
      .join(" ") ||
    d.supplier_contact_name ||
    ""
  );
}

function draftProjects(d: OrderDraft): string {
  return (d.auftraege || [])
    .map((a) => a.auftragsnummer_display || formatAuftragsnummerDisplay(a) || a.auftragsnummer)
    .filter(Boolean)
    .join(", ");
}

function draftPositionsSummary(d: OrderDraft): string {
  const n = (d.positionen || []).length;
  return n ? `${n} ${n === 1 ? "Position" : "Positionen"}` : "Keine Positionen";
}

export function OrderDraftPickerPanel({
  drafts,
  onLoad,
  onDelete,
}: {
  drafts: OrderDraft[];
  onLoad: (draft: OrderDraft) => void;
  onDelete: (id: number) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  if (!drafts.length) return null;

  return (
    <section className="mb-4 rounded-ios border border-accent/30 bg-accent/5">
      <header className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-start"
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="text-sm font-semibold text-label">
            Gespeicherte Entwürfe
            <span className="ms-1.5 font-normal text-label-secondary">({drafts.length})</span>
          </span>
          <span className="text-xs text-label-tertiary" aria-hidden="true">
            {expanded ? "▾" : "▸"}
          </span>
        </button>
        <p className="hidden text-xs text-label-secondary sm:block">oder unten eine neue Bestellung anlegen</p>
      </header>

      {expanded ? (
        <ul className="space-y-2 border-t border-accent/20 px-3 py-2" role="list">
          {drafts.map((d) => {
            const contact = draftContact(d);
            const projects = draftProjects(d);
            return (
              <li
                key={d.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-ios border border-separator bg-card px-3 py-2 shadow-sm"
              >
                <SupplierLogo
                  name={d.supplier_name}
                  partnerId={d.supplier_gp_id}
                  hasLogo={d.supplier_has_logo}
                  logoUpdatedAt={d.supplier_logo_updated_at}
                  className="h-8 w-8 rounded-lg text-[10px]"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-label">{d.supplier_name || "Ohne Lieferant"}</p>
                  <p className="mt-0.5 truncate text-xs text-label-secondary">
                    {[contact, projects, draftPositionsSummary(d)].filter(Boolean).join(" · ")}
                  </p>
                  <p className="mt-0.5 text-xs text-label-tertiary">Geändert {fmtDate(d.updated_at)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button type="button" size="sm" variant="primary" onClick={() => onLoad(d)}>
                    Laden
                  </Button>
                  {confirmId === d.id ? (
                    <>
                      <Button type="button" size="sm" variant="danger" onClick={() => void onDelete(d.id)}>
                        Ja, löschen
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmId(null)}>
                        Abbrechen
                      </Button>
                    </>
                  ) : (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmId(d.id)}>
                      Löschen
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className={clsx("border-t border-accent/20 px-3 py-2 text-xs text-label-secondary")}>
          {drafts.length === 1
            ? "1 Entwurf — zum Laden aufklappen"
            : `${drafts.length} Entwürfe — zum Laden aufklappen`}
        </p>
      )}
    </section>
  );
}
